import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import PCMBadge from "../components/PCMBadge";
import PCMBuilding from "../components/PCMBuilding";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { 
  Play, 
  CheckCircle, 
  Lock, 
  ArrowRight, 
  Target,
  Clock,
  BookOpen,
  AlertCircle,
  Compass,
  Battery,
  Brain,
  MessageCircle,
  Shield,
  Download,
  Calendar,
  Zap,
  Triangle,
  Layers,
  HelpCircle,
  Send,
  MessageSquare,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";

import { API } from "../utils/api";

// FLOW Module metadata
const FLOW_MODULES = {
  1: { icon: Compass },
  2: { icon: Battery },
  3: { icon: Brain },
  4: { icon: MessageCircle },
  5: { icon: Shield },
  6: { icon: Clock },
  7: { icon: Zap, isAnnex: true, annexLabel: "A1" },
  8: { icon: Triangle, isAnnex: true, annexLabel: "A2" },
};

// Annex modules linked to their parent modules
const ANNEX_PARENT_MAP = {
  7: 2, // A1 (Les 4 batteries) linked to Module 2
  8: 4, // A2 (Sortir des triangles) linked to Module 4
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [accessStatus, setAccessStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [accessError, setAccessError] = useState(null);
  // Coach interaction states
  const [coachComments, setCoachComments] = useState([]);
  const [myQuestions, setMyQuestions] = useState([]);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ subject: "", content: "" });
  const [submittingQuestion, setSubmittingQuestion] = useState(false);

  useEffect(() => {
    fetchData();
    fetchCoachInteractions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // First check access status
      const accessRes = await fetch(`${API}/users/me/access-status`, { headers, credentials: "include" });
      if (accessRes.ok) {
        const accessData = await accessRes.json();
        setAccessStatus(accessData);
      }
      
      const [modulesRes, progressRes, availabilityRes] = await Promise.all([
        fetch(`${API}/modules`, { headers, credentials: "include" }),
        fetch(`${API}/users/me/progress`, { headers, credentials: "include" }),
        fetch(`${API}/modules/availability`, { headers, credentials: "include" }),
      ]);

      if (modulesRes.ok) {
        const modulesData = await modulesRes.json();
        setModules(modulesData);
        setAccessError(null);
      } else if (modulesRes.status === 403) {
        const errorData = await modulesRes.json();
        setAccessError(errorData.detail);
      }

      if (progressRes.ok) {
        const progressData = await progressRes.json();
        setProgress(progressData);
      }

      if (availabilityRes.ok) {
        const availabilityData = await availabilityRes.json();
        setAvailability(availabilityData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoachInteractions = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch coach comments
      const commentsRes = await fetch(`${API}/users/me/coach-comments`, { headers, credentials: "include" });
      if (commentsRes.ok) {
        const commentsData = await commentsRes.json();
        setCoachComments(commentsData.comments || []);
        // Mark as read
        if (commentsData.unread_count > 0) {
          await fetch(`${API}/users/me/coach-comments/mark-read`, { 
            method: "POST",
            headers, 
            credentials: "include" 
          });
        }
      }
      
      // Fetch my questions
      const questionsRes = await fetch(`${API}/users/me/questions`, { headers, credentials: "include" });
      if (questionsRes.ok) {
        const questionsData = await questionsRes.json();
        setMyQuestions(questionsData);
      }
    } catch (error) {
      console.error("Error fetching coach interactions:", error);
    }
  };

  const submitQuestion = async () => {
    if (!newQuestion.subject.trim() || !newQuestion.content.trim()) {
      toast.error("Veuillez remplir le sujet et votre question");
      return;
    }
    
    setSubmittingQuestion(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      
      const response = await fetch(`${API}/users/me/questions`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify(newQuestion),
      });
      
      if (response.ok) {
        toast.success("Votre question a été envoyée à votre coach");
        setNewQuestion({ subject: "", content: "" });
        setShowQuestionForm(false);
        fetchCoachInteractions();
      } else {
        toast.error("Erreur lors de l'envoi de la question");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi de la question");
    } finally {
      setSubmittingQuestion(false);
    }
  };

  const getModuleProgress = (moduleId) => {
    return progress.find((p) => p.module_id === moduleId);
  };

  const getModuleAvailability = (moduleId) => {
    return availability.find((a) => a.module_id === moduleId);
  };

  const isModuleUnlocked = (moduleNumber) => {
    const moduleAvail = availability.find((a) => a.module_number === moduleNumber);
    return moduleAvail?.is_unlocked || false;
  };

  const getOverallProgress = () => {
    // Only count main modules (1-6) for overall progress
    const mainModules = modules.filter(m => m.number <= 6);
    if (mainModules.length === 0) return 0;
    const completedModules = progress.filter((p) => p.validation_completed && p.module_number <= 6).length;
    return Math.round((completedModules / mainModules.length) * 100);
  };

  const getCurrentModule = () => {
    // Only consider main modules (1-6) for current module
    const mainModules = modules.filter(m => m.number <= 6).sort((a, b) => a.number - b.number);
    for (const module of mainModules) {
      const moduleProgress = getModuleProgress(module.module_id);
      const moduleAvail = getModuleAvailability(module.module_id);
      if (!moduleProgress?.validation_completed && moduleAvail?.is_unlocked) {
        return module;
      }
    }
    return mainModules[mainModules.length - 1];
  };

  // Get annex module for a parent module
  const getAnnexForParent = (parentNumber) => {
    const annexNumber = Object.entries(ANNEX_PARENT_MAP).find(([annexNum, parent]) => parent === parentNumber)?.[0];
    if (!annexNumber) return null;
    return modules.find(m => m.number === parseInt(annexNumber));
  };

  const handleExportFullJournal = async () => {
    setExportingPdf(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/export/journal`, {
        headers,
        credentials: "include"
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `FLOW_Journal_${user?.name?.replace(/ /g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(t('module.answerSaved'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.exportError'));
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error(t('errors.exportError'));
    } finally {
      setExportingPdf(false);
    }
  };

  const needsOnboarding = !user?.pcm_base || !user?.pcm_phase;
  const hasCompletedModules = progress.filter(p => p.validation_completed).length > 0;
  const hasAccessProblem = accessError || (accessStatus?.access_status && accessStatus.access_status !== "active");

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#101E4D]"></div>
        </div>
      </div>
    );
  }

  const currentModule = getCurrentModule();

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      
      <main className="flex-1 p-6 lg:p-8 lg:ml-64" data-testid="dashboard-main">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-[#101E4D] mb-2">
            {t('dashboard.greeting', { name: user?.name?.split(" ")[0] })}
          </h1>
          <p className="text-slate-600">
            {t('dashboard.welcomeMessage')}
          </p>
        </div>

        {/* Access Problem Alert */}
        {hasAccessProblem && (
          <Card className="mb-8 border-red-300 bg-red-50" data-testid="access-alert">
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800 mb-2">
                    {accessStatus?.access_status === "pending" ? t('dashboard.accessPendingTitle') : 
                     accessStatus?.access_status === "blocked" ? t('dashboard.accessBlocked') : t('dashboard.accessRestricted')}
                  </h3>
                  <p className="text-red-700">
                    {accessError || t('dashboard.accessPendingMessage')}
                  </p>
                  <p className="text-sm text-red-600 mt-2">
                    {t('dashboard.contactCoach')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Onboarding Alert */}
        {!hasAccessProblem && needsOnboarding && (
          <Card className="mb-8 border-[#FF6B35] bg-orange-50" data-testid="onboarding-alert">
            <CardContent className="flex items-center justify-between p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-[#FF6B35] rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#101E4D]">{t('dashboard.incompleteProfile')}</h3>
                  <p className="text-sm text-slate-600">
                    {t('dashboard.incompleteProfileMessage')}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate("/profile")}
                className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white"
                data-testid="complete-profile-btn"
              >
                {t('common.edit')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* PCM Profile Card */}
          <Card className="col-span-1 md:col-span-2 bg-white" data-testid="pcm-profile-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-[#101E4D] flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#FF6B35]" />
                {t('dashboard.pcmProfile')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Show PCM Building if floors are configured, otherwise show badges */}
              {user?.pcm_floors && Object.keys(user.pcm_floors).length > 0 ? (
                <PCMBuilding 
                  pcmFloors={user.pcm_floors} 
                  pcmBase={user.pcm_base} 
                  pcmPhase={user.pcm_phase} 
                />
              ) : (
                <div className="flex flex-wrap gap-4">
                  <div className="flex-1 min-w-[140px]">
                    <p className="text-sm text-slate-500 mb-2">{t('dashboard.base')}</p>
                    {user?.pcm_base ? (
                      <PCMBadge type={user.pcm_base} label="Base" />
                    ) : (
                      <span className="text-slate-400 text-sm">{t('dashboard.notDefined')}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <p className="text-sm text-slate-500 mb-2">{t('dashboard.phase')}</p>
                    {user?.pcm_phase ? (
                      <PCMBadge type={user.pcm_phase} label="Phase" />
                    ) : (
                      <span className="text-slate-400 text-sm">{t('dashboard.notDefined')}</span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>


          {/* Progress Card */}
          <Card className="bg-white" data-testid="progress-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-[#101E4D]">
                {t('dashboard.progression')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center">
                <div className="relative w-20 h-20">
                  <svg className="progress-ring w-20 h-20">
                    <circle
                      className="text-slate-100"
                      strokeWidth="8"
                      stroke="currentColor"
                      fill="transparent"
                      r="32"
                      cx="40"
                      cy="40"
                    />
                    <circle
                      className="text-[#FF6B35] progress-ring__circle"
                      strokeWidth="8"
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="transparent"
                      r="32"
                      cx="40"
                      cy="40"
                      strokeDasharray={`${2 * Math.PI * 32}`}
                      strokeDashoffset={`${2 * Math.PI * 32 * (1 - getOverallProgress() / 100)}`}
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-[#101E4D]">
                    {getOverallProgress()}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Time Card */}
          <Card className="bg-white" data-testid="time-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-[#101E4D]">
                {t('dashboard.timeRemaining')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-3">
                <Clock className="w-8 h-8 text-[#FF6B35]" />
                <div>
                  <p className="text-2xl font-bold text-[#101E4D]">
                    {Math.max(0, 12 - progress.filter((p) => p.validation_completed).length * 2)}
                  </p>
                  <p className="text-sm text-slate-500">{t('dashboard.weeks')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Current Module */}
        {currentModule && (
          <Card className="mb-8 bg-white border-l-4 border-l-[#FF6B35]" data-testid="current-module-card">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-start space-x-4">
                  {(() => {
                    const flowMeta = FLOW_MODULES[currentModule.number] || {};
                    const CurrentModuleIcon = flowMeta.icon || BookOpen;
                    return (
                      <div className="w-14 h-14 bg-[#101E4D] rounded-xl flex items-center justify-center flex-shrink-0">
                        <CurrentModuleIcon className="w-7 h-7 text-white" />
                      </div>
                    );
                  })()}
                  <div>
                    <p className="text-sm text-[#FF6B35] font-medium mb-1">{t('dashboard.moduleInProgress', { number: currentModule.number })}</p>
                    <h3 className="text-xl font-semibold text-[#101E4D] mb-1">
                      {currentModule.title}
                    </h3>
                    <p className="text-slate-500 text-sm">
                      {currentModule.description}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate(`/module/${currentModule.module_id}`)}
                  className="bg-[#FF6B35] hover:bg-[#E85D2A] text-white px-6 lg:self-center"
                  data-testid="continue-module-btn"
                >
                  {t('dashboard.continueModule')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Coach Interactions Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Ask a Question */}
          <Card className="border-2 border-blue-100 bg-gradient-to-br from-blue-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-[#101E4D]">
                <HelpCircle className="w-5 h-5 text-blue-500" />
                {t('dashboard.askCoach', 'Une question pour votre coach ?')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {showQuestionForm ? (
                <div className="space-y-3">
                  <Input
                    placeholder={t('dashboard.questionSubject', 'Sujet de votre question')}
                    value={newQuestion.subject}
                    onChange={(e) => setNewQuestion({ ...newQuestion, subject: e.target.value })}
                    className="bg-white"
                    data-testid="question-subject-input"
                  />
                  <Textarea
                    placeholder={t('dashboard.questionContent', 'Décrivez votre question en détail...')}
                    value={newQuestion.content}
                    onChange={(e) => setNewQuestion({ ...newQuestion, content: e.target.value })}
                    className="bg-white min-h-[100px]"
                    data-testid="question-content-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={submitQuestion}
                      disabled={submittingQuestion}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="submit-question-btn"
                    >
                      {submittingQuestion ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      {t('dashboard.sendQuestion', 'Envoyer')}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setShowQuestionForm(false);
                        setNewQuestion({ subject: "", content: "" });
                      }}
                    >
                      {t('common.cancel', 'Annuler')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-3">
                    {t('dashboard.askCoachDescription', 'Vous avez une question sur votre parcours, un exercice ou le PCM ? Posez-la directement à votre coach.')}
                  </p>
                  <Button
                    onClick={() => setShowQuestionForm(true)}
                    variant="outline"
                    className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    data-testid="show-question-form-btn"
                  >
                    <HelpCircle className="w-4 h-4 mr-2" />
                    {t('dashboard.askQuestion', 'Poser une question')}
                  </Button>
                  
                  {/* Show pending questions */}
                  {myQuestions.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-xs font-medium text-gray-500">{t('dashboard.myQuestions', 'Mes questions')} :</p>
                      {myQuestions.slice(0, 2).map((q) => (
                        <div key={q.question_id} className="bg-white rounded-lg p-3 border text-sm">
                          <div className="flex justify-between items-start">
                            <span className="font-medium text-[#101E4D]">{q.subject}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              q.status === 'pending' 
                                ? 'bg-yellow-100 text-yellow-700' 
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {q.status === 'pending' ? t('dashboard.pending', 'En attente') : t('dashboard.answered', 'Répondu')}
                            </span>
                          </div>
                          {q.coach_response && (
                            <div className="mt-2 bg-green-50 rounded p-2 text-green-800">
                              <p className="text-xs font-medium mb-1">{t('dashboard.coachResponse', 'Réponse du coach')} :</p>
                              <p className="text-sm">{q.coach_response}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Coach Comments */}
          <Card className="border-2 border-orange-100 bg-gradient-to-br from-orange-50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-[#101E4D]">
                <MessageSquare className="w-5 h-5 text-orange-500" />
                {t('dashboard.coachFeedback', 'Retours de votre coach')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {coachComments.length > 0 ? (
                <div className="space-y-3 max-h-[250px] overflow-y-auto">
                  {coachComments.slice(0, 5).map((comment) => (
                    <div 
                      key={comment.comment_id} 
                      className="bg-white rounded-lg p-3 border-l-4 border-orange-400"
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-xs font-medium text-orange-600">
                          {comment.module_number 
                            ? `Module ${comment.module_number}` 
                            : t('dashboard.general', 'Général')}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(comment.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      <p className="text-sm text-[#101E4D] whitespace-pre-wrap">{comment.content}</p>
                      <p className="text-xs text-gray-400 mt-1">— {comment.coach_name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <MessageSquare className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    {t('dashboard.noCoachComments', 'Votre coach n\'a pas encore laissé de commentaires sur votre progression.')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Modules Grid */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-[#101E4D]">{t('dashboard.the6Modules')}</h2>
          {hasCompletedModules && (
            <Button
              onClick={handleExportFullJournal}
              disabled={exportingPdf}
              variant="outline"
              className="border-[#101E4D] text-[#101E4D] hover:bg-[#101E4D]/5"
              data-testid="export-journal-btn"
            >
              {exportingPdf ? (
                <div className="w-4 h-4 border-2 border-[#101E4D] border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {t('dashboard.exportJournal')}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.filter(m => m.number <= 6).map((module) => {
            const moduleProgress = getModuleProgress(module.module_id);
            const moduleAvail = getModuleAvailability(module.module_id);
            const isUnlocked = moduleAvail?.is_unlocked || false;
            const isCompleted = moduleProgress?.validation_completed;
            const isInProgress = moduleProgress && !isCompleted;
            const daysRemaining = moduleAvail?.days_remaining;
            const flowMeta = FLOW_MODULES[module.number] || {};
            const ModuleIcon = flowMeta.icon || BookOpen;
            
            // Check if this module has an annex
            const annexModule = getAnnexForParent(module.number);
            const annexAvail = annexModule ? getModuleAvailability(annexModule.module_id) : null;
            const annexMeta = annexModule ? FLOW_MODULES[annexModule.number] : null;

            return (
              <div key={module.module_id} className="space-y-3">
                <Card
                  className={`bg-white transition-all duration-300 ${
                    isUnlocked ? "hover:shadow-lg hover:-translate-y-1 cursor-pointer" : "opacity-60"
                  } ${isCompleted ? "border-green-200 bg-green-50/30" : ""}`}
                  onClick={() => isUnlocked && navigate(`/module/${module.module_id}`)}
                  data-testid={`module-card-${module.number}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        isCompleted ? "bg-green-500" : isUnlocked ? "bg-[#101E4D]" : "bg-slate-300"
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-6 h-6 text-white" />
                        ) : isUnlocked ? (
                          <ModuleIcon className="w-6 h-6 text-white" />
                        ) : (
                          <Lock className="w-5 h-5 text-white" />
                        )}
                      </div>
                      {isInProgress && (
                        <span className="text-xs bg-[#FF6B35]/10 text-[#FF6B35] px-3 py-1 rounded-full font-medium">
                          {t('dashboard.inProgress')}
                        </span>
                      )}
                      {isCompleted && (
                        <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                          {t('dashboard.completed')}
                        </span>
                      )}
                      {!isUnlocked && daysRemaining !== null && daysRemaining > 0 && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {daysRemaining}j
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-[#FF6B35] font-medium mb-1">Module {module.number}</p>
                    <h3 className="text-lg font-semibold text-[#101E4D] mb-3">{module.title}</h3>

                    {isInProgress && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>{t('dashboard.progression')}</span>
                          <span>
                            {moduleProgress.video_watched ? "50" : "0"}%
                          </span>
                        </div>
                        <Progress 
                          value={moduleProgress.video_watched ? 50 : 0} 
                          className="h-2"
                        />
                      </div>
                    )}

                    {/* Locked with countdown */}
                    {!isUnlocked && daysRemaining !== null && daysRemaining > 0 && (
                      <div className="flex items-center text-slate-500 text-sm">
                        <Clock className="w-4 h-4 mr-2" />
                        {t('dashboard.availableInDays', { days: daysRemaining })}
                      </div>
                    )}

                    {/* Locked waiting for previous module */}
                    {!isUnlocked && daysRemaining === null && (
                      <div className="flex items-center text-slate-400 text-sm">
                        <Lock className="w-4 h-4 mr-2" />
                        {t('dashboard.previousModuleRequired')}
                      </div>
                    )}

                    {isUnlocked && !isInProgress && !isCompleted && (
                      <div className="flex items-center text-[#FF6B35] text-sm font-medium">
                        <Play className="w-4 h-4 mr-2" />
                        {t('dashboard.startModule')}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Annex Module Link */}
                {annexModule && annexAvail?.is_unlocked && (
                  <div
                    onClick={() => navigate(`/module/${annexModule.module_id}`)}
                    className="flex items-center p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 cursor-pointer hover:shadow-md transition-all group"
                    data-testid={`annex-link-${annexMeta?.annexLabel}`}
                  >
                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3 group-hover:scale-110 transition-transform">
                      {annexMeta?.icon && <annexMeta.icon className="w-4 h-4 text-white" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-purple-600 font-semibold">{annexMeta?.annexLabel} — {t('dashboard.annexModule')}</p>
                      <p className="text-sm text-[#101E4D] font-medium">{annexModule.title.replace('Annexe — ', '')}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-purple-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                )}
              </div>
            );
          })}

          {modules.length === 0 && (
            <div className="col-span-full text-center py-12">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                {t('errors.moduleNotFound')}
              </h3>
              <p className="text-slate-500">
                {t('module.contentComingSoon')}
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
